import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { Checkbox } from '@rocco/components/checkbox';
import { cn } from '@rocco/utils/cn';

import {
    DOWNLOAD_FILE_CATEGORY_PARAM,
    DOWNLOAD_ITEM_TYPE_LABELS,
    DOWNLOAD_ITEM_TYPES,
    DOWNLOAD_ITEM_TYPE_PARAM,
    DOWNLOAD_PAGE_PARAM,
    type DownloadItemType,
} from '@modules/professional/config/download-filters';

interface DownloadFilterSidebarProps {
    itemTypes: DownloadItemType[];
    fileCategories: string[];
    /** Every category the professional has files in, with counts, unfiltered. */
    availableCategories: { Title: string; FileCount: number }[];
    /** File counts per item type, so a section can show what each is worth. */
    itemTypeCounts: Partial<Record<DownloadItemType, number>>;
}

/**
 * The Downloads tab's filter drawer.
 *
 * Section chrome, checkbox rows and type are the directory filter sidebar's, so
 * the drawer reads the same here as on the tabs whose filters come from search.
 * These facets do not: they are two plain multi-selects over the professional's
 * own files, which is why this is a separate component rather than a config
 * passed to that one.
 */
export const DownloadFilterSidebar = ({
    itemTypes,
    fileCategories,
    availableCategories,
    itemTypeCounts,
}: DownloadFilterSidebarProps) => {
    const [, setSearchParams] = useSearchParams();

    /** Any filter change sends the reader back to the first page of groups. */
    const toggle = (name: string, value: string) => {
        setSearchParams(
            (params) => {
                const current = params.getAll(name);
                params.delete(name);
                for (const entry of current) {
                    if (entry !== value) params.append(name, entry);
                }
                if (!current.includes(value)) params.append(name, value);
                params.delete(DOWNLOAD_PAGE_PARAM);
                return params;
            },
            { preventScrollReset: true },
        );
    };

    // 20rem matches the shared directory filter sidebar, so the drawer reads the
    // same width here as it does on every other tab.
    return (
        <div className="flex w-[20rem] max-w-full flex-col">
            <FilterSection title="Belongs to">
                {DOWNLOAD_ITEM_TYPES.map((type) => (
                    <CheckboxRow
                        key={type}
                        label={DOWNLOAD_ITEM_TYPE_LABELS[type]}
                        count={itemTypeCounts[type]}
                        checked={itemTypes.includes(type)}
                        onToggle={() => toggle(DOWNLOAD_ITEM_TYPE_PARAM, type)}
                    />
                ))}
            </FilterSection>

            {availableCategories.length > 0 && (
                <FilterSection title="File type">
                    {availableCategories.map((category) => (
                        <CheckboxRow
                            key={category.Title}
                            label={category.Title}
                            count={category.FileCount}
                            checked={fileCategories.includes(category.Title)}
                            onToggle={() =>
                                toggle(
                                    DOWNLOAD_FILE_CATEGORY_PARAM,
                                    category.Title,
                                )
                            }
                        />
                    ))}
                </FilterSection>
            )}
        </div>
    );
};

const FilterSection = ({
    title,
    children,
}: {
    title: string;
    children: React.ReactNode;
}) => {
    const [open, setOpen] = useState(true);

    return (
        <div className="border-t border-[#1c1c1c]">
            <button
                type="button"
                onClick={() => setOpen((prev) => !prev)}
                aria-expanded={open}
                className="flex w-full items-center justify-between py-[0.5625rem] text-left"
            >
                <span className="text-[1.125rem] leading-[1.04] font-normal text-[#323232] capitalize">
                    {title}
                </span>
                <span className="text-[#323232]">
                    <Chevron open={open} />
                </span>
            </button>
            {open && (
                <div className="flex flex-col gap-1.5 pt-3.5 pb-5">
                    {children}
                </div>
            )}
        </div>
    );
};

const CheckboxRow = ({
    label,
    count,
    checked,
    onToggle,
}: {
    label: string;
    count?: number;
    checked: boolean;
    onToggle: () => void;
}) => (
    <label className="flex cursor-pointer items-start gap-2">
        <Checkbox
            checked={checked}
            onCheckedChange={() => onToggle()}
            className="mt-0.5 size-[0.9375rem] shrink-0 rounded-[0.125rem] border border-[#141414]"
        />
        <span className="min-w-0 flex-1 text-[0.875rem] leading-[1.358] text-[#323232]">
            {label}
        </span>
        {count !== undefined && (
            <span className="shrink-0 text-[0.875rem] leading-[1.358] text-[#767676]">
                {count}
            </span>
        )}
    </label>
);

const Chevron = ({ open }: { open: boolean }) => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
        aria-hidden="true"
        className={cn(
            'shrink-0 transition-transform duration-200',
            open && '-scale-y-100',
        )}
    >
        <path
            d="M3 5l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);
